import { useEffect, useState } from 'react'
import { SortableList } from '@/components/common/SortableList'
import { AddButton } from '@/components/common/AddButton'
import { useReorderStages, useStages } from '../hooks'
import type { Stage, StageOwner } from '../types'
import { StageRow } from './StageRow'
import { StageFormDialog } from './StageFormDialog'
import { StageGatingAccordion } from './StageGatingAccordion'
import { ProductStageGatingPanel } from './ProductStageGatingPanel'

/**
 * Stage admin for a polymorphic owner. `owner` may be a bare configuration code
 * (back-compat) or an explicit {@link StageOwner}. Gating (transition conditions)
 * exists only for configuration stages, so it is shown only when `gating` is on and
 * the owner is a configuration.
 */
export function StagesPanel({ owner, gating = true }: { owner: string | StageOwner; gating?: boolean }) {
  const normalizedOwner: StageOwner = typeof owner === 'string' ? { kind: 'configuration', code: owner } : owner
  const showGating = gating

  const stagesQ = useStages(normalizedOwner)
  const reorder = useReorderStages(normalizedOwner)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Stage | null>(null)
  const [selectedCode, setSelectedCode] = useState<string | null>(null)

  useEffect(() => {
    if (!stagesQ.data) return
    if (selectedCode && stagesQ.data.some(s => s.code === selectedCode)) return
    setSelectedCode(stagesQ.data[0]?.code ?? null)
  }, [stagesQ.data, selectedCode])

  if (stagesQ.isLoading) return <div>Ładowanie etapów…</div>
  if (!stagesQ.data) return null

  const stages = stagesQ.data
  const selected = stages.find(s => s.code === selectedCode) ?? null
  const nextSort = (stages[stages.length - 1]?.sortOrder ?? 0) + 10

  return (
    <div className="flex flex-col gap-4 text-sm">
      <header className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {showGating
            ? 'Kolejność na liście = kolejność wyświetlania i przejść między etapami. Klik w etap = edycja warunków przejścia. Każdy etap może mieć kilka grup alternatyw (LUB między grupami, I wewnątrz grupy).'
            : 'Kolejność na liście = kolejność wyświetlania i przejść między etapami elementu.'}
        </p>
        <AddButton onClick={() => setCreating(true)}>Dodaj etap</AddButton>
      </header>

      <SortableList
        items={stages}
        getId={s => s.code}
        onReorder={ids => reorder.mutate(ids.map(String))}
        empty={<p className="text-xs text-slate-400 italic">Brak etapów - dodaj pierwszy.</p>}
        renderItem={s => (
          <StageRow
            stage={s}
            owner={normalizedOwner}
            isSelected={s.code === selectedCode}
            onSelect={() => setSelectedCode(s.code)}
            onEdit={setEditing}
          />
        )}
      />

      {showGating && selected && (
        <section className="border rounded">
          <header className="px-3 py-2 bg-slate-50 border-b text-sm">
            Warunki przejścia z etapu <strong>{selected.labelPl}</strong>{' '}
            <span className="font-mono text-xs text-slate-400">({selected.code})</span>
          </header>
          {normalizedOwner.kind === 'product' ? (
            <div className="p-3">
              <ProductStageGatingPanel productCode={normalizedOwner.code} stageCode={selected.code} />
            </div>
          ) : (
            <StageGatingAccordion
              configurationCode={normalizedOwner.code}
              stageCode={selected.code}
              stageLabel={selected.labelPl}
            />
          )}
        </section>
      )}

      {(creating || editing) && (
        <StageFormDialog
          owner={normalizedOwner}
          editing={editing}
          defaultSortOrder={nextSort}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}
